import { useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import BackLink from "../../components/navigation/BackLink/BackLink";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import { hasPermission, MATERIAL_PERMISSION_CODES } from "../../utils/permissions";
import {
  deleteMaterial,
  getMaterials,
} from "../../services/materialsService";
import { getActiveRubric } from "../../services/rubricService";
import { getDomainSubjects } from "../../services/organizationService";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { clampPageOnEmpty } from "../../utils/helpers/pagination";
import ConfirmModal from "../Templates/ConfirmModal";
import MaterialFormModal from "./MaterialFormModal";
import AdaptationModal from "./AdaptationModal";
import {
  MaterialCardMedia,
  MaterialCardSkeleton,
  materialHref,
} from "./MaterialPreview";

const PAGE_SIZE = 20;

export default function Materials() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  const canManage = hasPermission(user, MATERIAL_PERMISSION_CODES.MANAGE);

  const [materials, setMaterials] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [rubric, setRubric] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adapting, setAdapting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);

  // Reset to the first page whenever the search term changes so the user never
  // lands on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Active rubric (feeds the rule builder) + subject catalog (multiselect).
  // Loaded once — neither depends on the list query.
  useEffect(() => {
    const controller = new AbortController();
    getActiveRubric({ signal: controller.signal })
      .then((data) => setRubric(data))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setRubric(null);
      });
    getDomainSubjects({ signal: controller.signal })
      .then((data) => setSubjects(Array.isArray(data) ? data : data?.results || []))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setSubjects([]);
      });
    return () => controller.abort();
  }, []);

  const loadMaterials = useCallback(
    (signal) => {
      setIsLoading(true);
      getMaterials({
        params: { search: debouncedSearch || undefined, page },
        signal,
      })
        .then((data) => {
          const results = data?.results || [];
          const count = data?.count ?? 0;
          if (clampPageOnEmpty({ results, count, page, setPage })) return;
          setMaterials(results);
          setTotal(count);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          toast.error(t("materials.loadFailed"));
        })
        .finally(() => setIsLoading(false));
    },
    [debouncedSearch, page, t],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadMaterials(controller.signal);
    return () => controller.abort();
  }, [loadMaterials]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteMaterial({ uuid: deleting.uuid });
      toast.success(t("materials.removedToast", { title: deleting.title }));
      setDeleting(null);
      loadMaterials();
    } catch (err) {
      toast.error(formatApiError(err, t("materials.deleteFailed")));
    } finally {
      setIsDeleting(false);
    }
  };

  // Deep-link guard for users who reach /materials without the permission.
  if (!canManage) return <Navigate to="/" replace />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-6xl mx-auto">
        <BackLink />
        <div className="mt-4 mb-6 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-lg font-bold text-slate-900">
            {t("materials.heading")}{" "}
            <span className="text-slate-400 font-normal">({total})</span>
          </h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <svg
                className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.search")}
                className="pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none w-44 sm:w-56 transition"
              />
            </div>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500/40 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              {t("materials.addMaterial")}
            </button>
          </div>
        </div>

        <MaterialsGrid
          materials={materials}
          isLoading={isLoading}
          onEdit={setEditing}
          onAdapt={setAdapting}
          onDelete={setDeleting}
        />

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {t("materials.pageOf", { page, total: totalPages })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("materials.prev")}
              </button>
              <button
                type="button"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("materials.next")}
              </button>
            </div>
          </div>
        )}
      </div>

      {creating && (
        <MaterialFormModal
          material={null}
          subjects={subjects}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            setPage(1);
            loadMaterials();
          }}
        />
      )}

      {editing && (
        <MaterialFormModal
          material={editing}
          subjects={subjects}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadMaterials();
          }}
        />
      )}

      {adapting && (
        <AdaptationModal
          material={adapting}
          rubric={rubric}
          onClose={() => setAdapting(null)}
          onSaved={() => {
            setAdapting(null);
            loadMaterials();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t("materials.deleteMaterial")}
          body={t("materials.deleteConfirm", { title: deleting.title })}
          confirmLabel={isDeleting ? t("materials.removing") : t("materials.delete")}
          danger
          busy={isDeleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

// Effective recommendation rules for the caller's school: their own overlay when
// customized, otherwise the owner's defaults they inherit.
function MaterialRuleBadge({ material }) {
  const { t } = useTranslation();
  const adaptation = material.my_adaptation || {};
  const inheriting = adaptation.inheriting_default;
  const rules = inheriting
    ? material.default_adaptation || []
    : adaptation.rules || [];

  if (rules.length === 0) {
    return <span className="text-[11px] text-slate-500">{t("materials.noRules")}</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold">
      <span className="inline-flex items-center gap-1 text-indigo-600">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        {t("materials.ruleCount", { count: rules.length })}
      </span>
      <span
        className={`px-1.5 py-0.5 rounded-md text-[9px] uppercase tracking-wide ${
          inheriting ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"
        }`}
      >
        {inheriting ? t("materials.usingDefaults") : t("materials.customized")}
      </span>
    </span>
  );
}

function MaterialCard({ material, onEdit, onAdapt, onDelete }) {
  const { t } = useTranslation();
  const href = materialHref(material);

  return (
    <div className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition overflow-hidden">
      <MaterialCardMedia material={material}>
        <div className="mt-auto pt-1 space-y-2">
          {(material.subjects || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {material.subjects.map((s) => (
                <span
                  key={s.uuid}
                  className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-bold"
                >
                  {s.name}
                </span>
              ))}
            </div>
          )}
          <MaterialRuleBadge material={material} />
        </div>
      </MaterialCardMedia>

      <div className="flex items-center justify-end gap-1 px-3 py-2 border-t border-slate-100 bg-slate-50/60">
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="px-2 py-1 rounded-lg text-xs font-semibold text-slate-600 hover:text-indigo-600 hover:bg-white transition"
          >
            {t("materials.open")}
          </a>
        )}
        <button
          type="button"
          onClick={() => onAdapt(material)}
          className="px-2 py-1 rounded-lg text-xs font-semibold text-slate-600 hover:text-indigo-600 hover:bg-white transition"
        >
          {t("materials.recommendations")}
        </button>
        <button
          type="button"
          onClick={() => onEdit(material)}
          className="px-2 py-1 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-white transition"
        >
          {t("materials.edit")}
        </button>
        <button
          type="button"
          onClick={() => onDelete(material)}
          className="px-2 py-1 rounded-lg text-xs font-semibold text-rose-600 hover:bg-white transition"
        >
          {t("materials.delete")}
        </button>
      </div>
    </div>
  );
}

function MaterialsGrid({ materials, isLoading, onEdit, onAdapt, onDelete }) {
  const { t } = useTranslation();

  if (isLoading && materials.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <MaterialCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (materials.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50">
        <span className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M4 7a2 2 0 012-2h3l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" />
          </svg>
        </span>
        <p className="text-sm font-bold text-slate-700">{t("materials.noMaterials")}</p>
        <p className="mt-1 text-xs text-slate-400 max-w-xs">{t("materials.emptyHint")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {materials.map((m) => (
        <MaterialCard
          key={m.uuid}
          material={m}
          onEdit={onEdit}
          onAdapt={onAdapt}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
